import { useId, useRef, useState } from 'react';
import { Button, Input, Label, TextArea, TextField, toast } from '@heroui/react';
import { Sheet } from '@heroui-pro/react/sheet';
import { Segment } from '@heroui-pro/react/segment';
import { Timeline } from '@heroui-pro/react/timeline';
import { CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Trash2 } from 'lucide-react';
import {
  BLOCKS,
  SOURCE_LABEL,
  blockOfAlert,
  formatThresholds,
  validateAgreementText,
  type BoardAlert,
  type TandemSheet,
} from '@odd/domain';
import {
  useUpdateManualCase,
  useCreateAgreement,
  useDeleteTandemSheet,
  useSaveTandemSheet,
  useTimeline,
} from '../lib/queries';
import { fmtShort, plusDays } from '../lib/format';
import { useBoardCtx } from './BoardContext';
import { useErpLinks } from '../lib/erp';
import { ConfirmDialog, LightChip, Meter, SegmentBadge, Select, valueText, ErpLink } from './ui';
import type { DashboardLayout } from '../lib/layout';
import './CaseSheet.css';

const FOUR = [
  ['quePasa', 'Qué pasa'],
  ['queHemosIntentado', 'Qué hemos intentado'],
  ['porQueAtascado', 'Por qué está atascado'],
  ['queProponemos', 'Qué proponemos'],
] as const;
type FourKey = (typeof FOUR)[number][0];
type WorksheetForm = Record<FourKey, string>;
const worksheetForm = (sheet?: TandemSheet): WorksheetForm => ({
  quePasa: sheet?.quePasa ?? '',
  queHemosIntentado: sheet?.queHemosIntentado ?? '',
  porQueAtascado: sheet?.porQueAtascado ?? '',
  queProponemos: sheet?.queProponemos ?? '',
});
const QUESTION_HINTS: Record<FourKey, string> = {
  quePasa: 'Resume la situación actual y qué necesita atención.',
  queHemosIntentado: 'Recoge las gestiones que ya se han hecho y su resultado.',
  porQueAtascado: 'Identifica el bloqueo concreto que impide avanzar.',
  queProponemos: 'Describe el siguiente paso que planteáis en la reunión.',
};

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-card-2 px-3 py-2.5">
      <div className="text-[11px] font-bold tracking-wider text-ink-3 uppercase">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-ink">{children}</div>
    </div>
  );
}

function CaseTimeline({ a }: { a: BoardAlert }) {
  const q = useTimeline(a.objectType, a.objectId);
  if (q.isLoading) return <p className="text-sm text-ink-3">Cargando cronología…</p>;
  if (q.isError || !q.data?.length) return <p className="text-sm text-ink-3">Sin cronología disponible.</p>;
  const items = q.data;
  return (
    <Timeline size="sm">
      {items.map((e, i) => (
        <Timeline.Item key={`${e.at}-${i}`} status={i === items.length - 1 ? 'current' : 'muted'}>
          <Timeline.Rail>
            <Timeline.Marker />
            <Timeline.Connector />
          </Timeline.Rail>
          <Timeline.Content>
            <div className="text-sm text-ink">{e.text}</div>
            <div className="text-xs text-ink-3">{fmtShort(e.at)}</div>
          </Timeline.Content>
        </Timeline.Item>
      ))}
    </Timeline>
  );
}

function TandemSheetBlock({
  a,
  autoEdit = false,
  onSaved,
  saveLabel = 'Guardar hoja',
  onEdit,
}: {
  a: BoardAlert;
  /** Abrir directamente en edición (modo rellenar) */
  autoEdit?: boolean;
  onSaved?: () => void;
  saveLabel?: string;
  onEdit?: () => void;
}) {
  const { board, officeId } = useBoardCtx();
  const keys = [a.key, ...(a.related ?? []).map((r) => r.key)];
  const sheet = board.tandemSheets.find((s) => keys.includes(s.caseKey));
  const save = useSaveTandemSheet(officeId);
  const del = useDeleteTandemSheet(officeId);
  const [editing, setEditing] = useState(autoEdit);
  const [form, setForm] = useState<Record<FourKey, string>>({
    quePasa: sheet?.quePasa ?? '',
    queHemosIntentado: sheet?.queHemosIntentado ?? '',
    porQueAtascado: sheet?.porQueAtascado ?? '',
    queProponemos: sheet?.queProponemos ?? '',
  });

  if (!a.affId) return null;
  if (editing)
    return (
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(
            { week: board.week, affId: sheet?.affId ?? a.affId!, caseKey: sheet?.caseKey ?? a.key, ...form },
            {
              onSuccess: () => {
                if (!autoEdit) setEditing(false);
                toast.success('Hoja del tándem guardada');
                onSaved?.();
              },
              onError: (err) => toast.danger(err.message),
            },
          );
        }}
      >
        {FOUR.map(([k, label]) => (
          <TextField key={k} value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))}>
            <Label>{label}</Label>
            <TextArea rows={2} />
          </TextField>
        ))}
        <div className="flex gap-2">
          <Button type="submit" size="sm" isPending={save.isPending}>
            {saveLabel}
          </Button>
          <Button size="sm" variant="ghost" onPress={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    );

  if (!sheet)
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-line p-4">
        <div>
          <div className="text-sm font-bold text-ink">El tándem no lo ha traído</div>
          <div className="text-xs text-ink-3">Candidato a muestreo: «¿Qué has intentado ya?»</div>
        </div>
        <Button size="sm" variant="secondary" onPress={() => (onEdit ? onEdit() : setEditing(true))}>
          <ClipboardList className="size-4" /> Añadir a la hoja
        </Button>
      </div>
    );

  return (
    <div className="grid gap-3">
      {FOUR.map(([k, label]) => (
        <div key={k} className="border-l-2 border-orange pl-3">
          <div className="text-[11px] font-bold tracking-wider text-ink-3 uppercase">{label}</div>
          <div className="text-sm text-ink">{sheet[k] || '—'}</div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onPress={() => (onEdit ? onEdit() : setEditing(true))}>
          Editar
        </Button>
        <Button size="sm" variant="ghost" isPending={del.isPending} onPress={() => del.mutate(sheet.id)}>
          <Trash2 className="size-4" /> Quitar
        </Button>
      </div>
    </div>
  );
}

type AgreementDraft = { text: string; resp: string; due: string };
const agreementDraftFor = (a: BoardAlert, people: readonly { id: string }[]): AgreementDraft => ({
  text: '',
  resp: a.affId && people.some((person) => person.id === a.affId) ? a.affId : '',
  due: plusDays(7),
});

function AgreementForm({
  a,
  onDone,
  expanded = false,
  draft,
  onDraftChange,
  onBack,
  onPendingChange,
}: {
  a: BoardAlert;
  onDone: () => void;
  expanded?: boolean;
  draft?: AgreementDraft;
  onDraftChange?: (draft: AgreementDraft) => void;
  onBack?: () => void;
  onPendingChange?: (pending: boolean) => void;
}) {
  const { board, officeId } = useBoardCtx();
  const create = useCreateAgreement(officeId);
  const [localDraft, setLocalDraft] = useState(() => agreementDraftFor(a, board.people));
  const { text, resp, due } = draft ?? localDraft;
  const updateDraft = (patch: Partial<AgreementDraft>) => {
    const next = { text, resp, due, ...patch };
    if (onDraftChange) onDraftChange(next);
    else setLocalDraft(next);
  };
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err =
      validateAgreementText(text) ??
      (!resp
        ? 'Falta el responsable. Un nombre, nunca «el equipo».'
        : !due
          ? 'Falta la fecha concreta.'
          : null);
    setError(err);
    if (err || create.isPending) return;
    const person = board.people.find((p) => p.id === resp)!;
    onPendingChange?.(true);
    create.mutate(
      {
        week: board.week,
        text: text.trim(),
        responsibleId: person.id,
        responsibleName: person.name,
        dueDate: due,
        caseKey: a.key,
        caseLabel: `${a.caseTitle} · ${a.communityName}`,
      },
      {
        onSuccess: () => {
          toast.success('Acuerdo registrado');
          onDone();
        },
        onError: (e2) => setError(e2.message),
        onSettled: () => onPendingChange?.(false),
      },
    );
  };

  return (
    <form
      onSubmit={submit}
      aria-label={expanded ? 'Registrar un acuerdo' : undefined}
      className={expanded ? 'worksheet-agreement-form' : 'grid gap-3 rounded-2xl bg-card-2 p-4'}
      noValidate
    >
      <div className={expanded ? 'worksheet-agreement-fields' : 'contents'}>
        {expanded ? (
          <div className="worksheet-agreement-intro">
            <h3>Registrar un acuerdo</h3>
            <p>Una acción concreta, un responsable y una fecha para avanzar.</p>
          </div>
        ) : (
          <div className="text-sm font-extrabold text-ink">Registrar acuerdo</div>
        )}
        <TextField
          value={text}
          onChange={(value) => updateDraft({ text: value })}
          isInvalid={!!error && !text}
          isDisabled={create.isPending}
        >
          <Label>Acuerdo, en infinitivo</Label>
          {expanded ? (
            <TextArea rows={4} placeholder="Escalar a CSS la peritación del siniestro" />
          ) : (
            <Input placeholder="Escalar a CSS la peritación del siniestro" />
          )}
        </TextField>
        <div className="grid grid-cols-[1.4fr_1fr] gap-3">
          <div className="grid gap-1">
            <span className="text-sm font-medium text-ink">Responsable único</span>
            <Select
              label="Responsable"
              value={resp}
              onChange={(value) => updateDraft({ resp: value })}
              isDisabled={create.isPending}
              placeholder="Elegir…"
              options={board.people.map((p) => ({
                value: p.id,
                label: p.name,
                description: p.role ?? undefined,
              }))}
            />
          </div>
          <TextField
            value={due}
            onChange={(value) => updateDraft({ due: value })}
            isDisabled={create.isPending}
          >
            <Label>Plazo</Label>
            <Input type="date" />
          </TextField>
        </div>
        {!expanded && error && (
          <p role="alert" className="text-sm font-semibold text-bad">
            {error}
          </p>
        )}
        {!expanded && (
          <Button type="submit" isPending={create.isPending} className="justify-self-start">
            Registrar acuerdo
          </Button>
        )}
      </div>
      {expanded && (
        <footer className="worksheet-agreement-footer">
          {error ? (
            <p role="alert" className="text-bad">
              {error}
            </p>
          ) : (
            <p>El acuerdo quedará vinculado a este caso.</p>
          )}
          <div>
            <Button variant="ghost" onPress={onBack} isDisabled={create.isPending}>
              <ChevronLeft size={15} />
              Volver a la hoja
            </Button>
            <Button type="submit" isPending={create.isPending}>
              Registrar acuerdo
            </Button>
          </div>
        </footer>
      )}
    </form>
  );
}

/** Caso registrado a mano: quién y cuándo, semáforo editable y cierre */
function ManualCaseBox({ a, onClosed }: { a: BoardAlert; onClosed: () => void }) {
  const { officeId } = useBoardCtx();
  const update = useUpdateManualCase(officeId);
  const m = a.manual!;
  return (
    <div className="grid gap-3 rounded-2xl border border-warn bg-warn-soft/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segment
          size="sm"
          aria-label="Semáforo del caso"
          selectedKey={a.light}
          isDisabled={update.isPending}
          onSelectionChange={(k) => update.mutate({ id: a.objectId, light: k as 'rojo' | 'ambar' })}
        >
          <Segment.Item id="rojo">● rojo</Segment.Item>
          <Segment.Item id="ambar">● ámbar</Segment.Item>
        </Segment>
        <span className="tabular text-sm text-ink-2">abierto hace {a.value} d</span>
      </div>
      <div className="text-xs text-ink-3">
        Registrado a mano por {m.createdBy} el {fmtShort(m.createdAt)}. Sigue en la mesa cada semana hasta que
        se cierre.
      </div>
      {m.note && <div className="text-sm text-ink-2">{m.note}</div>}
      <Button
        size="sm"
        variant="secondary"
        className="justify-self-start"
        isPending={update.isPending}
        onPress={() =>
          update.mutate(
            { id: a.objectId, closed: true },
            {
              onSuccess: () => {
                toast.success('Caso cerrado');
                onClosed();
              },
              onError: (e) => toast.danger(e.message),
            },
          )
        }
      >
        <CheckCircle2 className="size-4" /> Cerrar caso
      </Button>
    </div>
  );
}

export interface FillNav {
  index: number;
  total: number;
  prev?: () => void;
  next?: () => void;
}

/** En modo rellenar, la escritura y las acciones tienen su propio espacio. */
function FocusedTandemSheet({ a, fill, onClose }: { a: BoardAlert; fill: FillNav; onClose: () => void }) {
  const { board, officeId, rules } = useBoardCtx();
  const erp = useErpLinks();
  const save = useSaveTandemSheet(officeId);
  const hintId = useId();
  const [drafts, setDrafts] = useState<Record<string, WorksheetForm>>({});
  const [confirmClose, setConfirmClose] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementPending, setAgreementPending] = useState(false);
  const [agreementDrafts, setAgreementDrafts] = useState<
    Record<string, { initial: AgreementDraft; value: AgreementDraft }>
  >({});
  const agreementEditorRef = useRef<HTMLElement>(null);
  const worksheetFormRef = useRef<HTMLFormElement>(null);
  const defaultAgreement = agreementDraftFor(a, board.people);
  const dirtyAgreementKeys = Object.keys(agreementDrafts).filter((key) => {
    const entry = agreementDrafts[key]!;
    return (['text', 'resp', 'due'] as const).some((field) => entry.value[field] !== entry.initial[field]);
  });
  const busy = save.isPending || agreementPending;
  const backToWorksheet = () => {
    setAgreementOpen(false);
    requestAnimationFrame(() => worksheetFormRef.current?.focus({ preventScroll: true }));
  };
  const keys = [a.key, ...(a.related ?? []).map((r) => r.key)];
  const sheet = board.tandemSheets.find((s) => keys.includes(s.caseKey));
  const caseKey = sheet?.caseKey ?? a.key;
  const form = drafts[caseKey] ?? worksheetForm(sheet);
  const dirtyKeys = Object.keys(drafts).filter((key) => {
    const saved = worksheetForm(board.tandemSheets.find((s) => s.caseKey === key));
    return FOUR.some(([field]) => drafts[key]![field] !== saved[field]);
  });
  const completed = FOUR.filter(([field]) => form[field].trim()).length;
  const requestClose = () => {
    if (busy) return;
    if (dirtyKeys.length || dirtyAgreementKeys.length) setConfirmClose(true);
    else onClose();
  };
  const navigate = (go?: () => void) => {
    if (busy || agreementOpen) return;
    save.reset();
    go?.();
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!a.affId || busy) return;
    save.mutate(
      { week: board.week, affId: sheet?.affId ?? a.affId, caseKey, ...form },
      {
        onSuccess: () => {
          setDrafts((previous) => {
            const next = { ...previous };
            delete next[caseKey];
            return next;
          });
          toast.success('Hoja del tándem guardada');
          if (fill.next) fill.next();
          else if (dirtyKeys.some((key) => key !== caseKey) || dirtyAgreementKeys.length)
            setConfirmClose(true);
          else onClose();
        },
      },
    );
  };

  return (
    <>
      <Sheet isOpen placement="right" isDismissable={!busy} onOpenChange={(open) => !open && requestClose()}>
        <Sheet.Backdrop>
          <Sheet.Content className="worksheet-sheet">
            <Sheet.Dialog className="worksheet-dialog">
              <Sheet.CloseTrigger aria-label="Cerrar hoja" isDisabled={busy} />
              <Sheet.Header className="worksheet-header">
                <div className="worksheet-topline">
                  <span className="worksheet-eyebrow">Hoja del tándem · Semana {board.week.slice(-2)}</span>
                  <nav className="worksheet-navigation" aria-label="Casos de la hoja">
                    <Button
                      size="sm"
                      variant="ghost"
                      isIconOnly
                      aria-label="Caso anterior"
                      isDisabled={!fill.prev || busy || agreementOpen}
                      onPress={() => navigate(fill.prev)}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="worksheet-case-count">
                      Caso {fill.index + 1} de {fill.total}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      isIconOnly
                      aria-label="Caso siguiente"
                      isDisabled={!fill.next || busy || agreementOpen}
                      onPress={() => navigate(fill.next)}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </nav>
                </div>
                <Sheet.Heading className="worksheet-heading">{a.caseTitle}</Sheet.Heading>
                <p className="worksheet-subtitle">
                  {a.communityName}
                  <span>·</span>
                  {a.affName ?? 'Sin tándem asignado'}
                </p>
              </Sheet.Header>
              <Sheet.Body className="worksheet-body">
                <div className="worksheet-layout" hidden={agreementOpen}>
                  <form
                    ref={worksheetFormRef}
                    tabIndex={-1}
                    id={hintId}
                    className="worksheet-form"
                    aria-label="Respuestas de la hoja"
                    onSubmit={submit}
                  >
                    <div className="worksheet-form-intro">
                      <h3>Prepara la conversación</h3>
                      <p>Cuatro respuestas breves para llegar al siguiente paso.</p>
                    </div>
                    {FOUR.map(([field, label], index) => (
                      <TextField
                        key={field}
                        className="worksheet-question"
                        value={form[field]}
                        isDisabled={save.isPending}
                        onChange={(value) =>
                          setDrafts((previous) => ({
                            ...previous,
                            [caseKey]: { ...(previous[caseKey] ?? form), [field]: value },
                          }))
                        }
                      >
                        <Label className="worksheet-question-label">
                          <span aria-hidden="true">0{index + 1}</span>
                          {label}
                        </Label>
                        <p id={`${hintId}-${field}`} className="worksheet-hint">
                          {QUESTION_HINTS[field]}
                        </p>
                        <TextArea
                          rows={3}
                          aria-describedby={`${hintId}-${field}`}
                          placeholder="Escribe una respuesta breve…"
                        />
                      </TextField>
                    ))}
                  </form>
                  <aside className="worksheet-context" aria-label="Contexto del caso">
                    <h3>Contexto del caso</h3>
                    <p className="worksheet-context-intro">Consúltalo mientras preparas la hoja.</p>
                    <details className="worksheet-disclosure">
                      <summary>
                        Datos del caso
                        <ChevronDown size={15} aria-hidden="true" />
                      </summary>
                      <div className="worksheet-disclosure-content">
                        <div className="worksheet-source-meta">
                          <SegmentBadge segment={a.segment} />
                          {a.objectCode && <span className="tabular">{a.objectCode}</span>}
                          <span>{BLOCKS.find((block) => block.id === blockOfAlert(a))?.name}</span>
                        </div>
                        <CaseFacts a={a} onClose={requestClose} />
                        <ErpLink href={erp.community(a.communityId)}>Abrir comunidad en Eleva</ErpLink>
                        <ErpLink href={erp.object(a)}>Abrir caso en Eleva</ErpLink>
                      </div>
                    </details>
                    <details className="worksheet-disclosure">
                      <summary>
                        Cronología
                        <ChevronDown size={15} aria-hidden="true" />
                      </summary>
                      <div className="worksheet-disclosure-content">
                        <CaseTimeline key={a.key} a={a} />
                      </div>
                    </details>
                    <div className="worksheet-agreement-action">
                      <Button
                        variant="secondary"
                        className="worksheet-agreement-launch"
                        isDisabled={busy}
                        onPress={() => {
                          setAgreementOpen(true);
                          requestAnimationFrame(() => agreementEditorRef.current?.focus());
                        }}
                      >
                        <ClipboardList size={15} />
                        Registrar un acuerdo
                        <ChevronRight size={15} />
                      </Button>
                      <p>Concreta el siguiente paso.</p>
                    </div>
                    <div className="worksheet-context-note">
                      <LightChip light={a.light} />
                      <span>{rules[a.ruleId]?.name}</span>
                    </div>
                  </aside>
                </div>
                <section
                  ref={agreementEditorRef}
                  className="worksheet-agreement-editor"
                  hidden={!agreementOpen}
                  tabIndex={-1}
                  aria-label="Editor de acuerdo"
                >
                  <AgreementForm
                    key={`agreement-${a.key}`}
                    a={a}
                    expanded
                    draft={agreementDrafts[a.key]?.value ?? defaultAgreement}
                    onDraftChange={(value) =>
                      setAgreementDrafts((previous) => ({
                        ...previous,
                        [a.key]: { initial: previous[a.key]?.initial ?? defaultAgreement, value },
                      }))
                    }
                    onPendingChange={setAgreementPending}
                    onBack={backToWorksheet}
                    onDone={() => {
                      setAgreementDrafts((previous) => {
                        const next = { ...previous };
                        delete next[a.key];
                        return next;
                      });
                      backToWorksheet();
                    }}
                  />
                </section>
              </Sheet.Body>
              <Sheet.Footer className="worksheet-footer" hidden={agreementOpen}>
                <div className="worksheet-save-state" role="status" aria-live="polite">
                  {save.error ? (
                    <p role="alert" className="text-bad">
                      {save.error.message}
                    </p>
                  ) : (
                    <>
                      <strong>{completed} de 4 respuestas</strong>
                      <span>
                        {dirtyKeys.includes(caseKey)
                          ? 'Cambios sin guardar'
                          : dirtyKeys.length
                            ? 'Hay cambios sin guardar en otros casos'
                            : dirtyAgreementKeys.length
                              ? 'Hay acuerdos sin guardar'
                              : 'Sin cambios pendientes'}
                      </span>
                    </>
                  )}
                </div>
                <div className="worksheet-footer-actions">
                  <Button variant="ghost" onPress={requestClose} isDisabled={save.isPending}>
                    Cerrar
                  </Button>
                  <Button type="submit" form={hintId} isPending={save.isPending} isDisabled={!a.affId}>
                    {fill.next ? 'Guardar y siguiente' : 'Guardar y terminar'}
                    <ChevronRight size={15} />
                  </Button>
                </div>
              </Sheet.Footer>
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>
      <ConfirmDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={onClose}
        title="¿Cerrar sin guardar?"
        confirmLabel="Descartar cambios y cerrar"
      >
        Tienes respuestas o acuerdos sin guardar. Puedes cancelar para seguir trabajando o descartarlos y
        cerrar.
      </ConfirmDialog>
    </>
  );
}

function CaseFacts({ a, onClose }: { a: BoardAlert; onClose: () => void }) {
  const { rules, board } = useBoardCtx();
  const rule = rules[a.ruleId];
  const tandem = board.tandems.find((t) => t.affId === a.affId);
  return (
    <>
      {a.manual ? (
        <ManualCaseBox a={a} onClosed={onClose} />
      ) : (
        <div className="grid gap-3 rounded-2xl border border-line p-4">
          <div className="flex items-center justify-between">
            <LightChip light={a.light} size="md" />
            <span className="tabular text-3xl font-extrabold text-ink">{valueText(rule, a.value)}</span>
          </div>
          <Meter value={a.value} rule={a} light={a.light} />
          <div className="flex justify-between text-xs text-ink-3">
            <span>{rule.measure}</span>
            <span>{formatThresholds({ ...rule, amber: a.amber, red: a.red, inv: a.inv })}</span>
          </div>
        </div>
      )}
      <div className="worksheet-facts grid grid-cols-2 gap-2">
        <Fact label="Estado">{a.statusText}</Fact>
        <Fact label="Próxima junta">
          {a.daysToMeeting != null ? `en ${a.daysToMeeting} días` : 'sin fecha'}
        </Fact>
        <Fact label="Tándem">
          {a.affName ?? '—'}
          {tandem?.coordinatorName ? ` · ${tandem.coordinatorName}` : ''}
        </Fact>
        <Fact label="Origen del dato">
          {SOURCE_LABEL[rule.source]} · {rule.sourceLabel}
        </Fact>
      </div>
      {a.related && a.related.length > 0 && (
        <div className="grid gap-2">
          <h3 className="text-xs font-bold tracking-wider text-orange-ink uppercase">
            Este caso también está fuera de umbral en
          </h3>
          {a.related.map((r) => (
            <div
              key={r.key}
              className="flex items-center justify-between gap-3 rounded-xl border border-line px-3 py-2"
            >
              <span className="text-sm text-ink">{rules[r.ruleId]?.name ?? r.ruleId}</span>
              <span className="flex items-center gap-2">
                <LightChip light={r.light} />
                <span className="tabular font-bold text-ink">{valueText(rules[r.ruleId], r.value)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function CaseSheet({
  alert: a,
  onClose,
  fill,
  layout = 'classic',
}: {
  alert: BoardAlert | null;
  onClose: () => void;
  /** Modo rellenar: la hoja del tándem va primero, en edición, con navegación entre los casos de la hoja */
  fill?: FillNav;
  layout?: DashboardLayout;
}) {
  const { rules, openCase } = useBoardCtx();
  const erp = useErpLinks();
  const rule = a ? rules[a.ruleId] : undefined;

  if (layout === 'v2' && a && rule && fill) return <FocusedTandemSheet a={a} fill={fill} onClose={onClose} />;

  return (
    <Sheet isOpen={!!a} onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-xl">
          <Sheet.Dialog>
            <Sheet.CloseTrigger />
            {a && rule && (
              <>
                <Sheet.Header>
                  <div className="text-xs font-bold tracking-wider text-orange-ink uppercase">
                    {rule.name} · {BLOCKS.find((b) => b.id === blockOfAlert(a))?.name}
                  </div>
                  <Sheet.Heading className="text-xl font-extrabold text-ink">{a.caseTitle}</Sheet.Heading>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-ink-3">
                    <SegmentBadge segment={a.segment} />
                    <ErpLink href={erp.community(a.communityId)} title="Abrir la comunidad en Eleva">
                      {a.communityName}
                    </ErpLink>
                    {a.objectCode && <span className="tabular">· {a.objectCode}</span>}
                  </div>
                  <ErpLink href={erp.object(a)} className="mt-1 text-sm font-semibold">
                    {a.objectType === 'ticket'
                      ? 'Abrir el ticket en Eleva'
                      : a.objectType === 'meeting'
                        ? 'Abrir la junta en Eleva'
                        : 'Abrir en Eleva'}
                  </ErpLink>
                </Sheet.Header>
                <Sheet.Body className="space-y-6 pb-8">
                  {fill && (
                    <section className="grid gap-3 rounded-2xl border border-orange bg-orange-soft/40 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-xs font-bold tracking-wider text-orange-ink uppercase">
                          Hoja del tándem · caso {fill.index + 1} de {fill.total}
                        </h3>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            isIconOnly
                            aria-label="Caso anterior"
                            isDisabled={!fill.prev}
                            onPress={fill.prev}
                          >
                            <ChevronLeft className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            isIconOnly
                            aria-label="Caso siguiente"
                            isDisabled={!fill.next}
                            onPress={fill.next}
                          >
                            <ChevronRight className="size-4" />
                          </Button>
                        </div>
                      </div>
                      <TandemSheetBlock
                        key={`fill-${a.key}`}
                        a={a}
                        autoEdit
                        saveLabel={fill.next ? 'Guardar y siguiente' : 'Guardar y terminar'}
                        onSaved={() => (fill.next ? fill.next() : onClose())}
                      />
                    </section>
                  )}
                  <CaseFacts a={a} onClose={onClose} />
                  {!fill && (
                    <section className="grid gap-3">
                      <h3 className="text-xs font-bold tracking-wider text-orange-ink uppercase">
                        Hoja del tándem
                      </h3>
                      <TandemSheetBlock
                        key={a.key}
                        a={a}
                        onEdit={layout === 'v2' ? () => openCase(a, { sequence: [a.key] }) : undefined}
                      />
                    </section>
                  )}
                  <section className="grid gap-3">
                    <h3 className="text-xs font-bold tracking-wider text-orange-ink uppercase">Cronología</h3>
                    <CaseTimeline a={a} />
                  </section>
                  <AgreementForm key={`f-${a.key}`} a={a} onDone={onClose} />
                </Sheet.Body>
              </>
            )}
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
